/**
 * Task Store - 公开任务状态管理 (v2)
 * @description 管理任务中心（公开页面）的任务列表和详情状态
 *              Designer 领域已隔离至 store/modules/designer.ts
 */

import type { TaskTypeItem } from '@/api/baseData'
import type { TaskAppendItem, TaskBidItem, TaskQAItem } from '@/components/pages/task/TaskDetail/types'
import type { PresetMilestone } from '@/types/milestone'
import type { TaskItem } from '@/types/task'

import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { baseDataApi } from '@/api/baseData'
import { getLatestTasks } from '@/api/task/index'
import { httpClient } from '@/utils/request'

// Re-export 子模块 Store，统一入口
export { useBidStore } from './bidStore'
export { useDeliveryStore } from './deliveryStore'

// 任务列表项接口（用于公开页面展示）
export interface PublicTask {
  id: string | number
  title: string
  description?: string
  status: string
  statusText?: string
  price: number
  budget?: number
  taskType?: string
  category?: string
  tags?: string[]
  publisher: {
    id: string
    name: string
    avatar: string
    creditScore: number
    publishedTaskCount: number
  }
  publisher_id?: string
  contractor_id?: string
  contractor?: {
    id: string
    name: string
    avatar: string
  }
  participants?: number
  location?: string
  deadline?: string
  publishTime?: string
  imageUrl?: string
  // 详情页扩展字段
  bidDeadline?: string
  viewCount?: number
  bidCount?: number
  depositAmount: number
  contractorType?: string
  deliverables?: string
  deliveryDate?: string
  images?: string[]
  attachments?: Array<{ id: string | number, name: string, url: string, size?: string }>
  fundingModel?: 'escrow' | 'non-escrow'
  milestones?: PresetMilestone[]
  bids?: TaskBidItem[]
  nda?: boolean
  copyright?: string
  budgetType?: 'fixed' | 'range' | 'negotiable'
  bidderLocationType?: 'none' | 'specified'
  bidderProvince?: string
  bidderCity?: string
  appendItems?: TaskAppendItem[]
  qaList?: TaskQAItem[]
}

// 查询参数接口
export interface TaskFetchParams {
  page?: number
  pageSize?: number
  sort?: string
  keyword?: string
  taskType?: string
  location?: string
  status?: string
  dateRange?: string
}

// API 响应接口
interface TaskListApiResponse {
  records?: PublicTask[]
  list?: PublicTask[]
  pagination?: {
    total: number
  }
  total?: number
  page?: number
  pageSize?: number
}

// 默认任务详情数据（防止模板渲染错误）
const defaultTaskDetail: PublicTask = {
  id: '',
  title: '',
  description: '',
  status: '',
  price: 0,
  publisher: {
    id: '',
    name: '',
    avatar: '',
    creditScore: 0,
    publishedTaskCount: 0,
  },
  publishTime: '',
  bidDeadline: '',
  viewCount: 0,
  bidCount: 0,
  taskType: '',
  depositAmount: 0,
  contractorType: '',
  deliverables: '',
  deliveryDate: '',
  images: [],
  attachments: [],
  fundingModel: 'non-escrow',
  milestones: [],
  bids: [],
}

export const useTaskStore = defineStore('public-task-v2', () => {
  // ==================== State ====================

  /** 任务列表 */
  const taskList = ref<PublicTask[]>([])
  /** 任务总数 */
  const taskTotal = ref(0)
  /** 列表加载状态 */
  const loading = ref(false)
  /** 错误信息 */
  const error = ref<string | null>(null)
  /** 当前页码 */
  const currentPage = ref(1)
  /** 每页条数 */
  const pageSize = ref(10)

  /** 当前任务详情 */
  const currentTask = ref<PublicTask>({ ...defaultTaskDetail })
  /** 详情加载状态 */
  const detailLoading = ref(false)

  /** 任务类型字典数据 */
  const taskTypes = ref<TaskTypeItem[]>([])

  /** 最新任务列表 */
  const latestTasks = ref<TaskItem[]>([])

  // ==================== Getters ====================

  /** 任务列表是否为空 */
  const isEmpty = computed(() => taskList.value.length === 0)

  // ==================== Actions ====================

  /**
   * 获取公开任务列表
   * @param params 查询参数
   */
  async function fetchTasks(params: TaskFetchParams = {}) {
    loading.value = true
    error.value = null

    try {
      const queryParams = {
        page: params.page || currentPage.value,
        pageSize: params.pageSize || pageSize.value,
        ...(params.sort ? { sortBy: params.sort } : {}),
        ...(params.keyword ? { keyword: params.keyword } : {}),
        ...(params.taskType ? { taskType: params.taskType } : {}),
        ...(params.location ? { location: params.location } : {}),
        ...(params.status ? { status: params.status } : {}),
        ...(params.dateRange ? { dateRange: params.dateRange } : {}),
      }

      const response = await httpClient.get<TaskListApiResponse>('/tasks', { params: queryParams })

      taskList.value = response.records || response.list || []
      taskTotal.value = response.pagination?.total || response.total || 0
      currentPage.value = params.page || 1
      pageSize.value = params.pageSize || 10
    }
    catch (e: unknown) {
      error.value = e instanceof Error ? e.message : '获取任务列表失败'
      taskList.value = []
      taskTotal.value = 0
    }
    finally {
      loading.value = false
    }
  }

  /**
   * 获取任务详情
   * @param id 任务ID
   */
  async function fetchTaskDetail(id: string | number) {
    detailLoading.value = true
    error.value = null

    try {
      const response = await httpClient.get<any>(`/tasks/${id}`)

      // Handle the wrapper object { task: {}, publisher: {}, attachments: [], milestones: [] }
      const data = response || {}
      const taskObj = data.task || data

      // Ensure budget is converted to yuan if needed, wait, public task expects price!
      const rawBudget = taskObj.budget ?? taskObj.price ?? 0
      const price = (rawBudget >= 100 && rawBudget % 100 === 0) ? rawBudget / 100 : rawBudget

      currentTask.value = {
        ...defaultTaskDetail,
        ...taskObj,
        taskType: taskObj.task_type || taskObj.taskType || defaultTaskDetail.taskType,
        contractorType: taskObj.contractor_type || taskObj.contractorType || defaultTaskDetail.contractorType,
        depositAmount: taskObj.margin_amount ?? defaultTaskDetail.depositAmount,
        bidDeadline: taskObj.bidding_deadline || taskObj.biddingDeadline || defaultTaskDetail.bidDeadline,
        fundingModel: taskObj.is_escrow || taskObj.fundingModel === 'escrow' ? 'escrow' : 'non-escrow',
        price,
        publishTime: taskObj.created_at || taskObj.publishTime || defaultTaskDetail.publishTime,
        deliverables: taskObj.deliverables || defaultTaskDetail.deliverables,
        deliveryDate: taskObj.delivery_date || taskObj.deliveryDate || defaultTaskDetail.deliveryDate,
        images: taskObj.images || defaultTaskDetail.images,
        copyright: taskObj.copyright,
        nda: taskObj.nda,
        publisher: data.publisher || taskObj.publisher || defaultTaskDetail.publisher,
        attachments: (data.attachments || taskObj.attachments || []).map((a: any) => ({
          id: a.id,
          name: a.file_name || a.name || 'attachment',
          url: a.file_url || a.url || '',
          size: a.file_size || a.size || 0,
          type: a.file_type || a.type || '',
        })),
        milestones: data.milestones || taskObj.milestones || [],
        bids: data.bids || taskObj.bids || [],
      }
    }
    catch (e: unknown) {
      error.value = e instanceof Error ? e.message : '获取任务详情失败'
      currentTask.value = { ...defaultTaskDetail }
    }
    finally {
      detailLoading.value = false
    }
  }

  /**
   * 获取任务类型列表
   * TODO: [Sprint_L2] 迁移为使用 useTaskTypes() composable
   */
  async function fetchTaskTypes() {
    try {
      const response = await baseDataApi.getTaskTypes()
      taskTypes.value = response.data ?? []
    }
    catch {
      taskTypes.value = []
    }
  }

  /**
   * 获取最新任务列表
   */
  async function fetchLatestTasks(count = 5) {
    try {
      const response = await getLatestTasks({ limit: count })
      // Backend /latest returns an array directly since httpClient unwraps { data: ... }
      latestTasks.value = response ?? []
    }
    catch {
      latestTasks.value = []
    }
  }

  /**
   * 重置所有状态
   */
  function resetState() {
    taskList.value = []
    taskTotal.value = 0
    loading.value = false
    error.value = null
    currentPage.value = 1
    pageSize.value = 10
    currentTask.value = { ...defaultTaskDetail }
    detailLoading.value = false
  }

  return {
    // State
    taskList,
    taskTotal,
    loading,
    error,
    currentPage,
    pageSize,
    currentTask,
    detailLoading,
    taskTypes,
    latestTasks,
    // Getters
    isEmpty,
    // Actions
    fetchTasks,
    fetchTaskDetail,
    fetchTaskTypes,
    fetchLatestTasks,
    resetState,
  }
})
